/* eslint-disable @typescript-eslint/no-this-alias */
import backend from '../../backend';
import { GlobalMessage } from '../../types';
import { useGlobalMessageStore, useGlobalOverlayLoaderStore } from '../../store';
import _ from 'lodash';
import i18n from '@/plugins/i18n';
import { dictionary } from './dictionary';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { AxiosError } from 'axios';
dayjs.extend(advancedFormat);

addDictionary({ dictionary });

function addDictionary({ dictionary, dictionaryPath = 'models._BaseModel' }) {
	for (const [locale, messages] of Object.entries(dictionary)) {
		const modelMessages = _.set({}, dictionaryPath, messages);
		const allMessages = _.merge({}, i18n.getLocaleMessage(locale), modelMessages);
		i18n.setLocaleMessage(locale, allMessages);
	}
}

class SaveInformation extends GlobalMessage {}
class ArchiveInformation extends GlobalMessage {}

function addError(error) {
	const globalMessageStore = useGlobalMessageStore();
	let message = error && error.message ? error.message : error.toString();
	if (error instanceof AxiosError) {
		message = error.response.data.message;
	}
	globalMessageStore.addMessage(new GlobalMessage({ title: i18n.t('error'), message, severity: 'error' }));
}
function addInformation(globalMessage) {
	const globalMessageStore = useGlobalMessageStore();
	globalMessageStore.addMessage(globalMessage);
}

export class _BaseModel {
	static t(path, args) {
		return i18n.tc(path, args);
	}

	static getPath(path) {
		const pathParts = path.split('.');
		let currentPath = '';
		for (let i = 0; i < pathParts.length; i++) {
			if (currentPath !== '') currentPath += '.';
			currentPath += pathParts[i];

			const pathPartOption = _.get(this.paths, currentPath);
			const pathWithinNextModel = pathParts.slice(i + 1).join('.');
			const childModel = _.get(pathPartOption, '__bind.model');
			if (
				pathWithinNextModel.length &&
				childModel
				// _.get(pathPartOption, '__bind.__component') === 'model-autocomplete'
			) {
				return childModel.getPath(pathWithinNextModel);
			}
		}
		const pathOptions = _.get(this.paths, currentPath);
		// console.log('currentPath', currentPath, pathOptions);
		return pathOptions;
	}

	static get getFunctions() {
		return {
			boolean: (value) => this.t('$global.boolean.' + value.toString()),
			dateCalendarWeek: (date) => {
				if (date) {
					const dateFormat = this.t('$global.date.longInt');
					return (
						this.t('$global.dateTerms.calendarWeekShort') +
						dayjs(date).isoWeek() +
						' | ' +
						dayjs(date).format(dateFormat)
					);
				} else {
					return '-';
				}
			},
			dateCalendarWeekDay: (date) => {
				if (date) {
					const dateFormat = this.t('$global.date.longInt');
					return (
						this.t('$global.dateTerms.calendarWeekShort') +
						dayjs(date).isoWeek() +
						' | ' +
						this.t('$global.days.' + dayjs(date).day()) +
						' | ' +
						dayjs(date).format(dateFormat)
					);
				} else {
					return '-';
				}
			},
			dateLongInt: (date) => {
				if (date) {
					const dateFormat = this.t('$global.date.longInt');
					return dayjs(date).format(dateFormat);
				} else {
					return '-';
				}
			},
			calendarWeek: (date) => {
				if (date) {
					// todo: move "KW" into dictionary
					return 'KW ' + dayjs(date).isoWeek();
				} else {
					return '-';
				}
			},
		};
	}

	static addDictionary({ modelName, dictionary }) {
		const dictionaryPath = 'models.' + modelName;
		addDictionary({ dictionary, dictionaryPath });
	}

	static getById({ _id, id, optionName, match } = {}) {
		return new Promise((resolve, reject) => {
			const providedId = id ? id : _id;
			const model = this;
			backend.crud
				.getById({ modelName: model.toString(), _id: providedId, optionName, match })
				.then((data) => {
					resolve(model.create(data));
				})
				.catch((error) => {
					addError(error);
					reject(error);
				});
		});
	}
	static getArrayByFilter({ filter = { 'meta.archived': false }, optionName, match } = {}) {
		return new Promise((resolve, reject) => {
			const model = this;
			backend.crud
				.getArrayByFilter({ modelName: model.toString(), filter, optionName, match })
				.then((data) => {
					const instancedItems = data.map((item) => model.create(item)) || [];
					resolve(instancedItems);
				})
				.catch((error) => {
					addError(error);
					reject(error);
				});
		});
	}
	static getObjectByFilter({ filter = { 'meta.archived': false }, optionName, match } = {}) {
		return new Promise((resolve, reject) => {
			const model = this;
			backend.crud
				.getObjectByFilter({ modelName: model.toString(), filter, optionName, match })
				.then((data) => {
					const instancedItems = _.mapValues(data, (item) => model.create(item)) || {};
					resolve(instancedItems);
				})
				.catch((error) => {
					addError(error);
					reject(error);
				});
		});
	}
	save({ optionName, match } = {}) {
		const globalOverlayLoaderStore = useGlobalOverlayLoaderStore();
		const document = this;

		return new Promise((resolve, reject) => {
			const overlayLoaderUUID = _.uniqueId();
			globalOverlayLoaderStore.overlayLoaderUUIDArray.push(overlayLoaderUUID);
			backend.crud
				.save({ modelName: document.constructor.toString(), items: document, optionName, match })
				.then((data) => {
					const newInstance = this.constructor.create(data);
					for (const [path, value] of Object.entries(newInstance)) {
						_.set(document, path, value);
					}
					addInformation(
						new SaveInformation({
							title: i18n.t('models._BaseModel.saved.title'),
							message: i18n.t('models._BaseModel.saved.message'),
							icon: 'mdi-database-check',
							severity: 'information',
						})
					);
					resolve(document);
				})
				.catch((error) => {
					addError(error);
					reject(error);
				})
				.finally(() => {
					globalOverlayLoaderStore.overlayLoaderUUIDArray.splice(
						globalOverlayLoaderStore.overlayLoaderUUIDArray.indexOf(overlayLoaderUUID),
						1
					);
				});
		});
	}
	archive({ optionName, match, suppressNotification } = {}) {
		const globalOverlayLoaderStore = useGlobalOverlayLoaderStore();
		const document = this;

		return new Promise((resolve, reject) => {
			const overlayLoaderUUID = _.uniqueId();
			globalOverlayLoaderStore.overlayLoaderUUIDArray.push(overlayLoaderUUID);
			if (!this._id) {
				this.meta.archived = true;
				resolve();
			}
			backend.crud
				.archive({ modelName: document.constructor.toString(), _ids: [document._id], optionName, match })
				.then((data) => {
					for (const [path, value] of Object.entries(data)) {
						_.set(document, path, value);
					}
					if (!suppressNotification) {
						addInformation(
							new ArchiveInformation({
								title: i18n.t('models._BaseModel.archived.title'),
								message: i18n.t('models._BaseModel.archived.message'),
								icon: 'mdi-database-check',
								severity: 'information',
							})
						);
					}
					resolve();
				})
				.catch((error) => {
					addError(error);
					reject(error);
				})
				.finally(() => {
					globalOverlayLoaderStore.overlayLoaderUUIDArray.splice(
						globalOverlayLoaderStore.overlayLoaderUUIDArray.indexOf(overlayLoaderUUID),
						1
					);
				});
		});
	}

	static getArrayMixin({ optionName, match, filter = { 'meta.archived': false } } = {}) {
		const model = this;
		const mixin = {
			data() {
				return {
					[_.camelCase(model.toString() + 'Array')]: [],
					[_.camelCase('is' + model.toString() + 'ArrayLoading')]: false,
					[_.camelCase('is' + model.toString() + 'ArrayLoadingError')]: false,
				};
			},
			methods: {
				/**
				 * @type Array
				 */
				[_.camelCase('load' + model.toString() + 'Array')]: function () {
					return new Promise((resolve, reject) => {
						this[_.camelCase('is' + model.toString() + 'ArrayLoading')] = true;
						model
							.getArrayByFilter({
								optionName,
								match,
								filter,
							})
							.then((result) => {
								this[_.camelCase(model.toString() + 'Array')] = result;
								this[_.camelCase('is' + model.toString() + 'ArrayLoadingError')] = false;
							})
							.catch((error) => {
								this[_.camelCase(model.toString() + 'Array')] = [];
								this[_.camelCase('is' + model.toString() + 'ArrayLoadingError')] = error;
								addError(error);
								reject(error);
							})
							.finally(() => {
								this[_.camelCase('is' + model.toString() + 'ArrayLoading')] = false;
								resolve(this[_.camelCase(model.toString() + 'Array')]);
							});
					});
				},
			},
			created() {
				this[_.camelCase('load' + model.toString() + 'Array')]();
			},
		};

		return mixin;
	}

	static getObjectMixin({ optionName, match, filter = { 'meta.archived': false } } = {}) {
		const model = this;
		const mixin = {
			data() {
				return {
					[_.camelCase(model.toString() + 'Object')]: {},
					[_.camelCase('is' + model.toString() + 'ObjectLoading')]: false,
					[_.camelCase('is' + model.toString() + 'ObjectLoadingError')]: false,
				};
			},
			methods: {
				/**
				 * @type Array
				 */
				[_.camelCase('load' + model.toString() + 'Object')]: function () {
					return new Promise((resolve, reject) => {
						this[_.camelCase('is' + model.toString() + 'ObjectLoading')] = true;
						model
							.getObjectByFilter({
								optionName,
								match,
								filter,
							})
							.then((result) => {
								this[_.camelCase(model.toString() + 'Object')] = result;
								this[_.camelCase('is' + model.toString() + 'ObjectLoadingError')] = false;
							})
							.catch((error) => {
								this[_.camelCase(model.toString() + 'Object')] = {};
								this[_.camelCase('is' + model.toString() + 'ObjectLoadingError')] = error;
								addError(error);
								reject(error);
							})
							.finally(() => {
								this[_.camelCase('is' + model.toString() + 'ObjectLoading')] = false;
								resolve(this[_.camelCase(model.toString() + 'Object')]);
							});
					});
				},
			},
			created() {
				this[_.camelCase('load' + model.toString() + 'Object')]();
			},
		};

		return mixin;
	}
}
